import {computed, inject, Injectable, linkedSignal, Signal} from '@angular/core';
import {IDeleteUserRequest, IDeleteUserResponse, IQueryUsersRequestParams, IQueryUsersResponse, IResetTempUsersLanguageResponse, Stamp, UserModel, UserPortrait} from '@core/api/data-access';
import {EntitySignalStore, useIdbPersistence} from '@feature-signal-store';
import {ToastService} from '@utils';
import {catchError, finalize, forkJoin, map, Observable, of, shareReplay, take, tap} from 'rxjs';
import {UsersService} from '../../services/users.service';
import {IStateUser, IUsersGlobalState, usersGlobalInitialState} from './users.state';

@Injectable({
	providedIn: 'root',
})
export class UserStore extends EntitySignalStore<IStateUser, IUsersGlobalState> {
	/* ------------ Providers / Services ------------ */
	public readonly usersService: UsersService = inject(UsersService);
	public readonly toastService: ToastService = inject(ToastService);
	/* ---------------- Properties ---------------- */
	private readonly _userPortraitsLoad = new Map<string, Observable<UserPortrait>>();

	/* ---------------- Loading State ---------------- */
	public readonly loadingState = computed<'loading' | 'success' | 'error'>(() => {
		const state = this.state();
		if (state.loading) return 'loading';
		if (state.error) return 'error';
		return 'success';
	});

	/* ---------------- Computed ---------------- */
	public readonly usersMap$ = computed<Map<string, IStateUser>>(() => {
		return Object.values(this.state().Entities.entityMap).reduce((acc, entityUser) => {
			if (!entityUser || !entityUser.id) return acc;
			acc.set(entityUser.id, entityUser);
			return acc;
		}, new Map<string, IStateUser>());
	});

	/* ---------------- Users Filtering ---------------- */
	public readonly users$ = computed<UserModel[]>(() =>
		Array.from(this.usersMap$().values())
			.filter(stateUser => stateUser?.user)
			.map(stateUser => stateUser.user),
	);

	public readonly tempUsers$ = computed<UserModel[]>(() => this.users$().filter(user => user.isTemporary && user.enabled));
	public readonly permanentUsers$ = computed<UserModel[]>(() => this.users$().filter(user => !user.isTemporary && user.enabled));
	public readonly disabledUsers$ = computed<UserModel[]>(() => this.users$().filter(user => !user.enabled));

	/* ---------------- Constructor ---------------- */
	constructor() {
		super(
			'Users Store',
			'Global',
			'id',
			{
				isEntityStore: true,
				plugins: [useIdbPersistence()],
			},
			usersGlobalInitialState,
		);
	}

	/* -------------------------------- Http Methods -------------------------------- */
	public queryUsers(request: IQueryUsersRequestParams): Observable<IQueryUsersResponse> {
		return this.usersService.queryUsers(request).pipe(
			map(response => {
				const usersToStore: IStateUser[] = response.users.flatMap(user => {
					const backendUser: UserModel = new UserModel(user);
					const stateUser = this.createStateUser(backendUser.id, {user: backendUser});
					if (stateUser) {
						stateUser.loadingState = 'success';
						return [stateUser];
					}
					return [];
				});
				this.addEntities(usersToStore);
				this.mutate(state => {
					state.lastQueryRequest = request;
					const responseWithoutUsers = {...response};
					delete responseWithoutUsers.users;
					state.lastQueryResponse = responseWithoutUsers;
				});
				return response;
			}),
		);
	}

	/**
	 * @description Searches for a user by either:
	 * - id
	 * - username
	 * - email
	 * - phone number.
	 * @param search
	 * @returns
	 */
	public loadUser(search: string, force = true, preventLogErrors = false): Observable<IStateUser> {
		if (!search) return of(null);
		const stateUser = (this.usersMap$() ?? new Map<string, IStateUser>()).get(search);
		if (stateUser && !force) {
			if (stateUser.loadingState === 'loading') return of(stateUser);
			if (stateUser.loadingState === 'success') return of(stateUser);
			if (stateUser.loadingState === 'error') {
				this.removeEntity(stateUser.id);
			}
		}
		return this.usersService.getUser(search).pipe(
			map(response => {
				const backendUser: UserModel = new UserModel(response.user);
				const stateUser = this.createStateUser(backendUser.id, {user: backendUser}, true);
				stateUser.loadingState = 'success';
				this.addEntity(stateUser);
				return stateUser;
			}),
			catchError(error => {
				if (!preventLogErrors) console.error(`Failed to load user with search term "${search}":`, error);
				return of(null);
			}),
		);
	}

	public loadUserModel(userId: string): Observable<UserModel> {
		return this.loadUser(userId).pipe(
			map(stateUser => {
				if (stateUser) return stateUser.user instanceof UserModel ? stateUser.user : new UserModel(stateUser.user);
				return null;
			}),
		);
	}

	public loadUserSignal(userId: string): Signal<UserModel> {
		const stateUser$ = linkedSignal(() => this.usersMap$().get(userId)?.user);
		if (stateUser$()) return stateUser$;
		this.loadUser(userId)
			.pipe(take(1))
			.subscribe(response => {
				if (response) stateUser$.set(response.user);
			});
		return stateUser$;
	}

	// Creates a request for all users if the userId is not found in the store
	public loadUserPortrait<T extends string>(...userIds: T[]): Observable<Record<string, UserPortrait | undefined>> {
		if (!userIds?.length) return of({});

		const uniqueUserIds = [...new Set(userIds)];
		const requests$: Observable<Record<string, UserPortrait | undefined>>[] = uniqueUserIds.map(userId => {
			// בדיקה אם הפורטרייט קיים בסטייט
			const stateUser = this.usersMap$().get(userId);
			if (stateUser?.portrait) {
				return of({[userId]: stateUser.portrait});
			}

			// בדיקה אם יש כבר בקשה פעילה לפורטרייט זה
			let existingRequest$ = this._userPortraitsLoad.get(userId);
			if (!existingRequest$ && (!stateUser || stateUser.user?.hasPortrait)) {
				// יצירת בקשה חדשה ושמירה ב-Map
				const newRequest$ = this.usersService.getPortrait(userId).pipe(
					// שיתוף הבקשה בין כל המנויים
					shareReplay(1),
					map(portrait => {
						// עדכון הסטייט עם הפורטרייט החדש
						this.mutate(state => {
							if (state.Entities.entityMap[userId]) {
								state.Entities.entityMap[userId].portrait = portrait;
							}
						});
						return portrait;
					}),
					// ניקוי המפה לאחר השלמת הבקשה
					finalize(() => {
						this._userPortraitsLoad.delete(userId);
					}),
				);

				// שמירת הבקשה במפה
				this._userPortraitsLoad.set(userId, newRequest$);
				existingRequest$ = newRequest$;
			}

			return existingRequest$ ? existingRequest$.pipe(map(portrait => ({[userId]: portrait}))) : of({[userId]: undefined});
		});

		if (!requests$.length) return of({});
		return forkJoin(requests$).pipe(map(responses => responses.reduce((acc, response) => ({...acc, ...response}), {})));
	}

	/**
	 * @description Checks if a user identifier property already exists in the system.
	 * The user identifiers are:
	 * - username
	 * - email
	 * - phone number
	 * @param userIdentifierValue The value of the user identifier property to check.
	 */
	public isUserIdentifierPropertyExist(userIdentifierValue: string): Observable<boolean> {
		// use the loadUser method in the store to check if the username is taken
		return this.loadUser(userIdentifierValue, false, true).pipe(
			map(response => {
				return !!response.user;
			}),
			catchError(() => of(false)),
		);
	}

	/* -------------------------------- State Methods -------------------------------- */

	/* -------------------------------- Private Methods -------------------------------- */
	private createStateUser(
		id: string,
		data: {
			user?: UserModel;
			portrait?: UserPortrait;
			stamps?: Stamp[];
		},
		override = false,
	): IStateUser {
		if (!id) return null;
		const {user, portrait, stamps} = data;
		const userState: IStateUser = this.usersMap$()?.get(id) ?? {
			id,
			loadingState: 'loading',
		};
		if (user) {
			if (override) userState.user = new UserModel(user);
			else {
				userState.user = new UserModel({
					...userState.user,
					...user,
				});
			}
		}
		if (portrait) {
			if (override) userState.portrait = new UserPortrait(portrait);
			else
				userState.portrait = new UserPortrait({
					...(userState.portrait || {}),
					...portrait,
				});
		}
		if (stamps) {
			if (override) userState.stamps = stamps;
			else userState.stamps = [...userState.stamps, ...stamps];
		}
		userState.id = id;
		return userState;
	}

	public deleteUser(userid: IDeleteUserRequest['userid']): Observable<IDeleteUserResponse> {
		return this.usersService.deleteUser(userid).pipe(
			map(response => {
				if (response) {
					this.removeEntity(userid);
				}
				return response;
			}),
		);
	}

	public restoreUser(id: string) {
		// get the user from the store
		const user = this.getEntity(id);
		if (!user) {
			throw new Error(`User with id ${id} not found`);
		}
		// update the user to be enabled
		user.user.enabled = true;
		return this.updateUser(user.user);
	}

	public updatePassword(userid: string, password: string): Observable<UserModel> {
		const user = this.getEntity(userid);
		if (!user) {
			throw new Error(`User with id ${userid} not found`);
		}
		const updatePasswordRequest = {
			user: JSON.parse(JSON.stringify(user.user)), // Ensure we send a clean copy of the user
			password,
		};
		updatePasswordRequest.user['password'] = password; // Set the password in the request
		return this.usersService.updateUser(updatePasswordRequest).pipe(map(response => response.user));
	}

	/* -------------------------------- Temp Users Methods -------------------------------- */
	/**
	 * יצירת משתמש זמני חדש
	 * @param emailOrPhone כתובת דואר אלקטרוני או מספר טלפון
	 * @param name שם המשתמש (אופציונלי)
	 */
	public createTempUser(emailOrPhone: string, name = ''): Observable<UserModel> {
		return this.usersService.createTempUser(emailOrPhone, name).pipe(
			map(response => {
				const user = new UserModel(response.user);
				const stateUser = this.createStateUser(user.id, {user: user}, true);
				stateUser.loadingState = 'success';
				this.addEntity(stateUser);
				return user;
			}),
		);
	}

	/**
	 * איפוס שפה למשתמשים זמניים
	 */
	public resetTempUsersLanguage(): Observable<IResetTempUsersLanguageResponse> {
		return this.usersService.resetTempUsersLanguage();
	}

	/* -------------------------------- User Create / Update Methods -------------------------------- */
	/**
	 * יצירת משתמש חדש
	 * @param user נתוני המשתמש
	 * @param password סיסמה
	 */
	public createUser(user: UserModel, password: string): Observable<UserModel> {
		user['password'] = password;
		return this.usersService.createUser({user, password}).pipe(
			map(response => {
				const newUser = new UserModel(response.user);
				const stateUser = this.createStateUser(newUser.id, {user: newUser}, true);
				stateUser.loadingState = 'success';
				this.removeEntity(newUser.id);
				this.addEntity(stateUser);
				return newUser;
			}),
		);
	}

	/**
	 * עדכון משתמש קיים
	 * @param user נתוני המשתמש לעדכון
	 * @param password סיסמה חדשה (אופציונלי)
	 * @param isProfileUpdate
	 * @param showStatusToasts
	 */
	public updateUser(user: UserModel, password = '', isProfileUpdate = false, showStatusToasts = false): Observable<UserModel> {
		if (password) user['password'] = password;
		const request$ = isProfileUpdate
			? this.usersService.updateSelfProfile({
					user,
					password,
				})
			: this.usersService.updateUser({user, password});
		return request$.pipe(
			catchError(error => {
				console.error(error);
				if (showStatusToasts) this.toastService.open({message: 'struct.user-edit.updateFeild', type: 'error'});
				return of(null);
			}),
			map(response => {
				if (!response) return null;
				const updatedUser = new UserModel(response.user);
				const stateUser = this.createStateUser(updatedUser.id, {user: updatedUser});
				stateUser.loadingState = 'success';
				this.updateEntity(stateUser.id, entity => {
					entity.user = new UserModel(user);
					return {...entity};
				});
				return updatedUser;
			}),
			tap(() => {
				if (showStatusToasts) this.toastService.open({message: 'struct.user-edit.updatesuccess', type: 'success'});
			}),
		);
	}

	/**
	 * עדכון מספר משתמשים בבת אחת
	 * @param users מערך משתמשים לעדכון
	 */
	public updateMultiUsers(users: any[]): Observable<UserModel[]> {
		if (!users || users.length === 0) {
			return of([]);
		}

		const updateObservables = users.map(user => this.updateUser(user));
		return forkJoin(updateObservables);
	}

	/* -------------------------------- User Stamps Methods -------------------------------- */
	/**
	 * טעינת חותמות משתמש
	 * @param userId מזהה המשתמש
	 */
	public loadUserStamps(userId: string): Observable<Stamp[]> {
		return this.usersService.getStamps(userId).pipe(
			map(response => {
				// Ensure stamps conform to the required type
				const stamps = response.stamps.map(stamp => ({
					...stamp,
					// Cast imageType to the appropriate enum value
					imageType: stamp.imageType as 'jpg' | 'png' | 'jpeg',
				}));

				this.mutate(state => {
					if (state.Entities.entityMap[userId]) {
						state.Entities.entityMap[userId].stamps = stamps;
					}
				});
				return stamps;
			}),
		);
	}

	/**
	 * יצירת חותמת משתמש
	 * @param userId מזהה המשתמש
	 * @param name שם החותמת
	 * @param image נתוני תמונת החותמת
	 * @param imageType סוג תמונת החותמת
	 */
	public createUserStamp(userId: string, name: string, image: string, imageType: string): Observable<Stamp> {
		// Ensure imageType is one of the valid types before passing to API
		const validatedImageType = ['jpg', 'png', 'jpeg'].includes(imageType) ? (imageType as 'jpg' | 'png' | 'jpeg') : 'png';

		return this.usersService
			.createStamp({
				userId,
				name,
				image,
				imageType: validatedImageType,
			})
			.pipe(
				map(response => {
					// יוצרים חותמת חדשה מתוך התשובה
					const newStamp: Stamp = new Stamp(response);

					// עדכון החותמות בסטייט
					this.mutate(state => {
						if (state.Entities.entityMap[userId]) {
							if (!state.Entities.entityMap[userId].stamps) {
								state.Entities.entityMap[userId].stamps = [];
							}
							state.Entities.entityMap[userId].stamps.push(newStamp);
						}
					});

					return newStamp;
				}),
			);
	}

	/**
	 * מחיקת חותמת משתמש
	 * @param userId מזהה המשתמש
	 * @param stampName שם החותמת למחיקה
	 */
	public deleteUserStamp(userId: string, stampName: string): Observable<Stamp[]> {
		return this.usersService.deleteStamp(userId, stampName).pipe(
			map(() => {
				this.mutate(state => {
					const user = state.Entities.entityMap[userId];
					if (!user || !user.stamps) return;
					user.stamps = user.stamps.filter(stamp => stamp.name !== stampName);
					state.Entities.entityMap[userId] = user;
				});
				return this.state().Entities.entityMap[userId].stamps;
			}),
		);
	}
}
